import { useEffect, useEffectEvent } from "react";
import { create } from "zustand";

import {
  AnalyticsEventType,
  useGoogleAnalyticsEvent,
} from "../analytics-event";
import { useFirebaseAuth } from "../user";

/**
 * Authenticating is observed here rather than reported from the sign-in UI, because
 * the UI may be unmounted by the session change before its own callback runs: a
 * returning person's session switches to another User mid-flow.
 *
 * This relies on sign-in running in a popup, which is the auth adapter's mode.
 * A redirect strategy would reload the page mid-flow, leaving the session that comes
 * back the first one observed rather than a transition, and every event below would
 * silently stop firing.
 */
export function useAuthenticationAnalyticsBridgeEffect(): void {
  const { userId, authenticated } = useFirebaseAuth();
  const reportAuthenticated = useEffectEvent(useReportAuthenticated());

  useEffect(() => {
    // Read before writing, and write before the guards below return: the comparison
    // needs the previous session, and a session skipped here is one the next
    // comparison is made against.
    const { observedSession, observeSession } = useStore.getState();
    observeSession({ userId, authenticated });

    // The first session observed is whoever Firebase resolved to on load, not a
    // transition — someone arriving already signed in has authenticated nothing.
    if (observedSession === undefined) return;
    if (observedSession.authenticated || !authenticated) return;

    reportAuthenticated(observedSession.userId === userId);
  }, [userId, authenticated]);
}

// A uid carried across the upgrade means the anonymous User was linked in place, so
// this is a sign-up. A uid that changed means the session switched to a User who
// already existed.
//
// The port is read directly rather than through `useAnalyticsEvent`, whose board-event
// counting reads the User's Firestore document and so suspends on every uid this
// device has not seen. Neither event below is counted, and suspending would delay the
// observation past the transition it exists to catch.
function useReportAuthenticated(): (upgradedInPlace: boolean) => void {
  const { submitEvent } = useGoogleAnalyticsEvent();

  return (upgradedInPlace: boolean) => {
    try {
      submitEvent(
        upgradedInPlace
          ? { type: AnalyticsEventType.SignUp }
          : { type: AnalyticsEventType.SignIn },
      );
    } catch (error) {
      // TODO: Direct report to sentry
    }
  };
}

const useStore = create<StoreState>((set) => ({
  observeSession: (observedSession: Session) => set({ observedSession }),
}));

type StoreState = {
  observedSession?: Session;
  observeSession: (session: Session) => void;
};

type Session = {
  userId: string;
  authenticated: boolean;
};
